import { api } from './api';

export interface LabTest {
  test_name: string;
  value: number | null;
  unit: string;
  reference_range: string | null;
  status: 'normal' | 'high' | 'low' | 'critical' | 'abnormal' | null;
  performed_at: string | null;
  lab_name: string | null;
  loinc?: {
    code: string;
    display: string;
    system: string;
  };
  confidence?: number;
  isDuplicate?: boolean;
}

export interface ExtractionResult {
  tests: LabTest[];
}

export interface ExtractionJob {
  id: string;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  clinic: {
    id: string;
    name: string;
  };
  fileName: string;
  fileType: string;
  fileSize: number;
  status: 'pending' | 'processing' | 'extracted' | 'review_pending' | 'approved' | 'rejected' | 'failed';
  statusLabel: string;
  extractionMethod: 'claude_vision' | 'textract' | 'manual_entry' | null;
  extractionResult: ExtractionResult | null;
  extractionConfidence: number | null;
  detectedProvider: string | null;
  detectedCollectionDate: string | null;
  pageCount: number | null;
  errorMessage: string | null;
  retryCount: number;
  duplicateFlags: string[];
  hasDuplicates: boolean;
  hasHighConfidence: boolean;
  hasLowConfidence: boolean;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  reviewer: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  sourceContext: 'intake' | 'clinic_upload' | 'batch_import' | 'fax';
  intakeId: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  presignedUrl?: string; // Only present on single job fetch
}

export interface ExtractionJobListResponse {
  jobs: ExtractionJob[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
  };
}

class LabExtractionService {
  /**
   * Get extraction jobs (review queue)
   */
  async getExtractionJobs(params?: {
    status?: string;
    patientId?: string;
    clinicId?: string;
    page?: number;
    limit?: number;
  }): Promise<ExtractionJobListResponse> {
    const response = await api.get('/labs/extractions', { params });
    return response.data.data;
  }

  /**
   * Get single extraction job by ID (includes presigned PDF URL)
   */
  async getExtractionJobById(jobId: string): Promise<ExtractionJob> {
    const response = await api.get(`/labs/extractions/${jobId}`);
    return response.data.data;
  }

  /**
   * Poll job status (lightweight endpoint for checking progress)
   */
  async getJobStatus(jobId: string): Promise<{
    id: string;
    status: ExtractionJob['status'];
    confidence: number | null;
    error: string | null;
    updatedAt: string;
  }> {
    const response = await api.get(`/labs/extractions/${jobId}/status`);
    return response.data.data;
  }

  /**
   * Update extraction results during review
   */
  async updateExtractionResults(
    jobId: string,
    data: {
      tests: LabTest[];
      detectedProvider?: string;
      detectedCollectionDate?: string;
    }
  ): Promise<ExtractionJob> {
    const response = await api.put(`/labs/extractions/${jobId}/results`, data);
    return response.data.data;
  }

  /**
   * Approve extraction job (writes to patient record)
   */
  async approveExtractionJob(jobId: string): Promise<ExtractionJob> {
    const response = await api.post(`/labs/extractions/${jobId}/approve`);
    return response.data.data;
  }

  /**
   * Reject extraction job
   */
  async rejectExtractionJob(jobId: string, reason?: string): Promise<ExtractionJob> {
    const response = await api.post(`/labs/extractions/${jobId}/reject`, { reason });
    return response.data.data;
  }

  /**
   * Retry failed extraction job
   */
  async retryExtractionJob(jobId: string): Promise<ExtractionJob> {
    const response = await api.post(`/labs/extractions/${jobId}/retry`);
    return response.data.data;
  }
}

export const labExtractionService = new LabExtractionService();
export default labExtractionService;
